import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { ClientDocumentService, IClientDocumentUploadResult, UploadData } from '../../core/services/client-document.service';
import { HttpEventType } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';
import { ClientDocument } from '../../core/models/client-document';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';

const kilobyte = 1024;
const megabyte = 1024 * kilobyte;


@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss']
})
export class DocumentUploadComponent {
  private destroy$ = new Subject<void>();

  private readonly MAX_UPLOAD_SIZE = (28 * megabyte) + (8 * kilobyte);
  private readonly maxUploadSizeText = '28MB';
  private readonly MAX_INDIVIDUAL_UPLOAD_SIZE = (5 * megabyte) + (8 * kilobyte);
  private readonly maxIndividualUploadSizeText = '5MB';

  maxTotalUploadSize = this.MAX_UPLOAD_SIZE;
  maxTotalUploadSizeText = this.maxUploadSizeText;
  private uploadSubscription?: any;
  public abortController?: AbortController; // Made public for testing

  @Input() document: ClientDocument | undefined;
  @Input() description: string | null = null;
  @Input() uploadType: string | null = null;
  @Input() matterId: string | null = null;
  @Input() clientId: number | null = null;

  @Output() onUpload = new EventEmitter<{ result: IClientDocumentUploadResult }>();
  @Output() onCancelled = new EventEmitter<void>();
  @Output() onError = new EventEmitter<any>();

  totalSize = 0;
  isBusy = false;
  errorMessage: string | null = null;
  files: File[] = [];
  uploadDisabled = true;
  removeDisabled = true;
  isDragOver = false;
  uploadProgress = 0;
  uploadData: UploadData | null = null;


  uploadSpeed = 0; // bytes per second
  estimatedTimeRemaining = 0; // seconds
  private uploadStartTime = 0;
  private lastProgressTime = 0;
  private lastLoadedBytes = 0;
  private breakPointState: BreakpointState | null = null;

  constructor(private clientDocumentService: ClientDocumentService,private breakpointObserver: BreakpointObserver) {

  }

  getAllowedFileTypesString(): string {
    return this.clientDocumentService.allowedFileTypes.join(', ');
  }

  getAllowedFileTypesComparisonString() : string{
     return this.clientDocumentService.allowedFileTypes
     .map(type => type.replace(/^\./, '')).join('|');
  }


  ngOnInit() {
    this.setUpLargerUploadLimit();
     this.breakpointObserver.observe([Breakpoints.Small, Breakpoints.XSmall]).subscribe((result: BreakpointState) => {
          this.breakPointState = result;
        });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  private setUpLargerUploadLimit() {
    // Placeholder code for retrieving a config setting on if should use larger upload limit or not
    const shouldUseLargerUploadLimit = true;
    if (shouldUseLargerUploadLimit) {
      this.maxTotalUploadSize = this.MAX_UPLOAD_SIZE;
      this.maxTotalUploadSizeText = this.maxUploadSizeText;
    } else {
      this.maxTotalUploadSize = this.MAX_INDIVIDUAL_UPLOAD_SIZE;
      this.maxTotalUploadSizeText = this.maxIndividualUploadSizeText;
    }
  }

  isDragging: boolean = false;
  selectedFiles: File[] = [];


  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const files = inputElement.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
    // PO-160 clear the input value so the same file can be selected again if it has been removed or the event will never be triggered.
    inputElement.value = '';
  }

  private handleFiles(files: File[]) {
    this.errorMessage = null;
    let showDuplicateErrorMessage = false;

    files.forEach(file => {
      const duplicateFileNameFound = this.hasDuplicateFileName(file.name);

      if (duplicateFileNameFound) {
        showDuplicateErrorMessage = true;
      } else {
        this.files.push(file);
        this.totalSize += file.size;
      }
    });

    this.validateFiles();

    if (showDuplicateErrorMessage) {
      this.errorMessage = 'Files with duplicate names have been removed. Please ensure all file names are unique.';
    }

  }

  private validateFiles() {

    let maxIndividualFileSize = 0;
    this.errorMessage = null;
    const regex = new RegExp(`\.(${this.getAllowedFileTypesComparisonString()})$`, 'i');

    // Check for invalid characters in file names
    if (this.files.length > 0) {
      const invalidFiles = this.files.filter(file => !this.clientDocumentService.isValidFileName(file.name)).map(file => file.name);
      if (invalidFiles.length > 0) {
        this.errorMessage = `The following file(s) have invalid characters in their name: ${invalidFiles.join(', ')}. Please rename the files and try again.`;
        this.uploadDisabled = true;
        this.removeDisabled = this.files.length === 0;
        return;
      }
    }

    

    // Check for file type
    if (this.files.length > 0 && this.files.some(file => !regex.test(file.name))) {
      this.errorMessage = `One or more files have an invalid file type. Please upload files with the following extensions: ${this.getAllowedFileTypesString()}`;
      this.uploadDisabled = true;
      this.removeDisabled = this.files.length === 0;
      return;
    }

    this.files.forEach(file => {
      maxIndividualFileSize = Math.max(maxIndividualFileSize, file.size);
    });

    // check if any files are larger tyhan the max individual size
    if (maxIndividualFileSize > this.MAX_INDIVIDUAL_UPLOAD_SIZE) {
      const sizeInMb = maxIndividualFileSize / megabyte;
      this.errorMessage = `The maximum file size limit for each file is ${this.maxIndividualUploadSizeText}. The file you are attempting to upload is ${sizeInMb.toFixed(2)}MB. Please compress the file to ensure it is below the ${this.maxIndividualUploadSizeText} limit.`;
      this.uploadDisabled = true;
      this.removeDisabled = this.files.length === 0;
      return;
    }

    //check if the total size exceeds the max
    if (this.totalSize > this.maxTotalUploadSize) {
      const totalSizeInMB = this.totalSize / megabyte;
      this.errorMessage = `The total file size for this requested item cannot exceed ${this.maxTotalUploadSizeText}. The files you are attempting to upload total ${totalSizeInMB.toFixed(2)}MB. Please compress the files to ensure the total is below the ${this.maxTotalUploadSizeText} limit.`;
      this.uploadDisabled = true;
      this.removeDisabled = this.files.length === 0;
      return;
    }

    this.uploadDisabled = this.files.length === 0;
    this.errorMessage = null;
    this.removeDisabled = this.files.length === 0;
  }

  private hasDuplicateFileName(fileName: string): boolean {
    return this.files.some(file =>
      file.name.toLowerCase() === fileName.toLowerCase()
    );
  }

  removeAll(): void {
    if (this.isBusy) {
      throw new Error("Cannot remove attachments while uploading");
    }

    this.errorMessage = null;
    this.files = [];
    this.removeDisabled = true;
    this.totalSize = 0;
    this.uploadDisabled = true;
    this.uploadProgress = 0;
  }

  removeOne(index: number): void {
    if (this.isBusy) {
      throw new Error("Cannot remove attachment while uploading");
    }

    this.totalSize -= this.files[index].size;
    this.files.splice(index, 1);
    if(this.files.length === 0) {
      this.errorMessage = null;
      this.removeDisabled = true;
      this.totalSize = 0;
      this.uploadDisabled = true;
      this.uploadProgress = 0;
    }else{
    this.validateFiles();
    }
  }

  uploadFiles(): void {

    if (this.isBusy || this.uploadDisabled || this.files.length < 1) {
      return;
    }

    this.uploadStartTime = Date.now();
    this.lastProgressTime = this.uploadStartTime;
    this.lastLoadedBytes = 0;
    this.uploadSpeed = 0;
    this.estimatedTimeRemaining = 0;

    this.isBusy = true;
    this.uploadDisabled = true;
    this.removeDisabled = true;
    this.errorMessage = null;
    this.uploadProgress = 0;

    // Create AbortController for cancellation
    this.abortController = new AbortController();

    // Prepare upload data
    this.uploadData = {
      files: this.files,
      docFromClientId: this.document?.id ?? undefined,
      description: this.document?.fileDesc ?? undefined,
      matterId: this.matterId ?? undefined,
      fileId: this.document?.fileId ?? undefined,
      type: this.uploadType ?? undefined

    };

    const timeoutId = setTimeout(() => {
      if (this.isBusy && this.uploadSubscription) {
        this.cancelUpload();
      }
    }, 180000);

    this.uploadSubscription = this.clientDocumentService.uploadFiles(this.uploadData, this.abortController.signal)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.updateProgressMetrics(event.loaded, event.total);
          } else if (event.type === HttpEventType.Response) {
            // Upload completed successfully
            clearTimeout(timeoutId);
            this.handleUploadSuccess(event.body as ClientDocument);
          }
        },
        error: (error) => {
          clearTimeout(timeoutId);
          this.handleUploadError(error);
        },
        complete: () => {
          clearTimeout(timeoutId);
        }
      });
  }

  private updateProgressMetrics(loaded: number, total: number): void {
    const now = Date.now();
    const timeDiff = now - this.lastProgressTime;
    const bytesDiff = loaded - this.lastLoadedBytes;

    // Calculate progress percentage
    this.uploadProgress = Math.round(100 * loaded / total);

    // Calculate upload speed (bytes per second)
    if (timeDiff > 0) {
      this.uploadSpeed = (bytesDiff / timeDiff) * 1000; // Convert to bytes/second

      // Calculate estimated time remaining
      const remainingBytes = total - loaded;
      this.estimatedTimeRemaining = Math.round(remainingBytes / this.uploadSpeed);
    }

    this.lastProgressTime = now;
    this.lastLoadedBytes = loaded;
  }


  private handleUploadSuccess(document: ClientDocument) {
    this.isBusy = false;
    this.totalSize = 0;
    this.uploadData = null;
    this.abortController = undefined;

    // Create result
    const result: IClientDocumentUploadResult = {
      document: document,
      files: [...this.files]
    };

    // Clean up files
    this.removeAll();

    // Emit success event
    this.onUpload.emit({ result });

  }

  private handleUploadError(error: any) {
    console.log('Upload error occurred:', error); // Debug log
    
    this.isBusy = false;
    this.removeDisabled = false;
    this.uploadDisabled = false;
    this.uploadProgress = 0;
    this.uploadData = null;
    this.abortController = undefined;

    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = null;
    }

    // Handle different types of errors
    if (error.name === 'AbortError' || error.status === 0 || error.status === 408) {
      // Request was cancelled (AbortError), connection terminated (status 0), or server timeout (408)
      this.errorMessage = "Upload cancelled.";
      this.onCancelled.emit();
    } else {
      // Other errors
      this.onError.emit(error);
    }
  }

  cancelUpload(): void {
    console.log('Cancel upload called'); // Debug log
    
    // Abort the HTTP request
    if (this.abortController) {
      this.abortController.abort();
    }



    // Use a small timeout to allow any pending error handler to run first
    // If the error handler runs, it will set isBusy to false, so we check for that
    setTimeout(() => {
      if (this.isBusy) {
        // Error handler didn't run, so we need to handle cleanup here
        console.log('Error handler did not run, handling cancellation cleanup');

    // Unsubscribe from the upload subscription
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = null;
    }

        this.handleCancellationCleanup();
      }
    }, 100);
  }

  private handleCancellationCleanup(): void {
    this.isBusy = false;
    this.removeDisabled = false;
    this.uploadDisabled = false;
    this.uploadProgress = 0;
    this.uploadData = null;
    this.abortController = undefined;
    
    // Emit cancellation event
    this.onCancelled.emit();
  }




  formatTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getTruncatedFileName(fileName: string, maxBaseLength: number = 20): string {

  if(this.breakPointState?.breakpoints[Breakpoints.XSmall]){
    maxBaseLength = 20;
  }
  else if(this.breakPointState?.breakpoints[Breakpoints.Small]){
    maxBaseLength = 40;
  }
  else{
    maxBaseLength = 60;
  }

  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return fileName;
  const base = fileName.substring(0, lastDot);
  const ext = fileName.substring(lastDot);
  if (base.length > maxBaseLength) {
    return base.substring(0, maxBaseLength) + '[...]' + ext;
  }
  return fileName;
}


}